import React, { useEffect, useCallback, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { cn } from './cn';

const DialogContext = createContext({ onOpenChange: null });

function Dialog({ open, onOpenChange, children }) {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && onOpenChange) onOpenChange(false);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return (
    <DialogContext.Provider value={{ onOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
}

function DialogPortal({ children }) {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(children, document.body);
}

function DialogOverlay({ className, onClick, style, ...props }) {
  return (
    <div
      className={cn('fixed inset-0 z-[2147483646] bg-black/60', className)}
      onClick={onClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483646,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        ...style,
      }}
      {...props}
    />
  );
}

function DialogClose({ className, onClick, children, ...props }) {
  const { onOpenChange } = useContext(DialogContext);
  const handleClick = (event) => {
    if (onClick) onClick(event);
    if (onOpenChange) onOpenChange(false);
  };
  return (
    <button type="button" className={className} onClick={handleClick} {...props}>
      {children}
    </button>
  );
}

function DialogTrigger({ children, ...props }) {
  return children;
}

function DialogContent({ className, children, onClose, style, ...props }) {
  const { onOpenChange } = useContext(DialogContext);

  const handleOverlayClick = () => {
    if (onClose) onClose();
    else if (onOpenChange) onOpenChange(false);
  };

  return (
    <DialogPortal>
      <React.Fragment>
        <DialogOverlay onClick={handleOverlayClick} />
        <div
          className={cn(
            'fixed left-1/2 top-1/2 z-[2147483647] grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-succession-border bg-succession-bg-primary p-6 shadow-succession-lg sm:rounded-lg',
            className
          )}
          style={{
            position: 'fixed',
            zIndex: 2147483647,
            ...style,
          }}
          {...props}
        >
          {children}
        </div>
      </React.Fragment>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }) {
  return <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />;
}

function DialogFooter({ className, ...props }) {
  return <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />;
}

function DialogTitle({ className, ...props }) {
  return <h2 className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />;
}

function DialogDescription({ className, ...props }) {
  return <p className={cn('text-sm text-succession-text-secondary', className)} {...props} />;
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
